<template>
  <button
    class="widget-button"
    :class="[
      `widget-button--color-${colorClassSuffix}`,
      `widget-button--view-${view}`,
      `widget-button--size-${size}`,
      {
        'widget-button--icon-only': isIconOnly,
        'widget-button--loading': isLoading,
        'widget-button--rounded': isRounded,
      },
    ]"
    :disabled="isDisabled || isLoading"
    :tabindex="tabIndex"
  >
    <span class="widget-button__content">
      <slot />
    </span>
    <span class="widget-button__loader">
      <span
        v-if="loadingTitle && !isIconOnly"
        class="widget-button__loader-title"
      >
        {{ loadingTitle }}
      </span>
      <widget-spinner
        class="widget-button__loader-icon"
        color="currentColor"
        :size="spinnerSize"
      />
    </span>
  </button>
</template>

<script lang="ts" setup>
import {
  isWidgetButtonCustomColor,
  type WidgetButtonColor,
  type WidgetButtonCustomColor,
  type WidgetButtonSize,
  type WidgetButtonView,
} from '@widgets/shared/types'
import WidgetSpinner from '@widgets/shared/components/WidgetSpinner.vue'
import { computed } from '@/composition/vue/compositionApi'

const {
  color = 'blue',
  view = 'solid',
  size = '40',
} = defineProps<{
  color?: WidgetButtonColor | WidgetButtonCustomColor
  view?: WidgetButtonView
  size?: WidgetButtonSize
  isDisabled?: boolean
  isIconOnly?: boolean
  isLoading?: boolean
  loadingTitle?: string
  isRounded?: boolean
  tabIndex?: string
}>()

const colorClassSuffix = computed(() => {
  if (isWidgetButtonCustomColor(color)) {
    return 'custom'
  }
  return color
})

const customPrimaryColor = computed(() => {
  if (isWidgetButtonCustomColor(color)) {
    return color.primary
  }
  return undefined
})

const customSecondaryColor = computed(() => {
  if (isWidgetButtonCustomColor(color)) {
    return color.secondary
  }
  return undefined
})

const spinnerSizeMap: Record<WidgetButtonSize, string> = {
  '40': '20px',
  '36': '16px',
  '32': '16px',
  '28': '16px',
  '20': '16px',
}

const spinnerSize = computed(() => spinnerSizeMap[size])
</script>

<style lang="scss" scoped>
@import '@/styles/custom-variables.scss';
@import '@/styles/mixins.scss';

$rootClassName: 'widget-button';

$widget-button-color-map: map-merge(
  $widget-color-map,
  (
    'danger': $red,
    'success': $green,
    'secondary': $gray6,
  )
);

$widget-button-size-map: (
  '40': (
    'height': 40px,
    'padding': 0 16px,
    'font-size': $md-font,
  ),
  '36': (
    'height': 36px,
    'padding': 0 16px,
    'font-size': 14px,
  ),
  '32': (
    'height': 32px,
    'padding': 0 16px,
    'font-size': 14px,
  ),
  '28': (
    'height': 28px,
    'padding': 0 10px,
    'font-size': $sm-font,
  ),
  '20': (
    'height': 20px,
    'padding': 0 10px,
    'font-size': $sm-font,
  ),
);

@mixin widget-button-color($background-color, $font-color) {
  &.#{$rootClassName}--view-solid {
    background-color: $background-color;
    color: $font-color;
    box-shadow: $button-shadow-15;

    &:hover:not([disabled]),
    &:active:not([disabled]) {
      background: linear-gradient(0deg, rgba(20, 20, 43, 0.1) 0%, rgba(20, 20, 43, 0.1) 100%), $background-color;
    }
    &:active:not([disabled]) {
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16) inset;
    }
  }

  &.#{$rootClassName}--view-outline {
    background: transparent;
    border: 1px solid $background-color;
    color: $background-color;
    box-shadow: $button-shadow-15;

    &:hover:not([disabled]),
    &:active:not([disabled]) {
      background: rgba(20, 20, 43, 0.05);
    }
    &:active:not([disabled]) {
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16) inset;
    }
  }

  &.#{$rootClassName}--view-transparent {
    background: transparent;
    border: none;
    color: $background-color;

    &:hover:not([disabled]),
    &:active:not([disabled]) {
      background: rgba(20, 20, 43, 0.05);
    }
    &:active:not([disabled]) {
      box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.16) inset;
    }
  }

  &.#{$rootClassName}--view-hover-transparent {
    background: transparent;
    border: none;
    color: $background-color;
  }
}

.#{$rootClassName} {
  padding: 0;
  border: none;
  background: none;
  line-height: 1.2;
  font-weight: $semi-bold-font;
  border-radius: $border-radius-x;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  box-sizing: border-box;
  transition:
    background $transition-100ms-ease-in-out,
    box-shadow $transition-100ms-ease-in-out,
    color $transition-100ms-ease-in-out;

  &[disabled] {
    opacity: 0.5;
  }

  &:focus-visible {
    outline: 2px solid $blue400;
    outline-offset: 2px;
  }

  @each $size-name, $size-value in $widget-button-size-map {
    &--size-#{$size-name} {
      min-height: map-get($size-value, 'height');
      padding: map-get($size-value, 'padding');
      font-size: map-get($size-value, 'font-size');

      &.#{$rootClassName}--icon-only {
        width: map-get($size-value, 'height');
        padding: 0;
      }

      .#{$rootClassName}__loader {
        padding: map-get($size-value, 'padding');
      }
    }
  }

  @each $color-name, $color-value in $widget-button-color-map {
    &--color-#{$color-name} {
      @include widget-button-color($color-value, $white);
    }
  }

  &--color-custom {
    @include widget-button-color(v-bind('customPrimaryColor'), v-bind('customSecondaryColor'));
  }

  &--rounded {
    border-radius: 999px;
  }

  &__content {
    @include truncate(1);
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  &__loader-title {
    @include truncate(1);
    margin-right: 8px;
  }

  &__loader-icon {
    flex-shrink: 0;
    flex-grow: 0;
  }

  &:not(.#{$rootClassName}--loading) {
    .#{$rootClassName}__loader {
      opacity: 0;
    }
  }

  &.#{$rootClassName}--loading {
    .#{$rootClassName}__content {
      opacity: 0;
    }
  }
}
</style>
